import { useState } from 'react';
import { Card, CardBody } from '@components/ui';
import {
  ChevronDownIcon,
  HomeIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  PlusCircleIcon,
  CubeIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  Cog6ToothIcon,
  BuildingStorefrontIcon,
  UsersIcon,
  CreditCardIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@lib/utils/cn';

interface ManualSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  content: ManualContent[];
}

interface ManualContent {
  subtitle?: string;
  text?: string;
  list?: string[];
  image?: string;
  imageAlt?: string;
  note?: string;
}

const manualData: ManualSection[] = [
  {
    id: 'dashboard',
    title: '1. Dashboard Factura Libre',
    icon: <HomeIcon className="h-6 w-6" />,
    content: [
      {
        subtitle: 'Filtro de fechas',
        text: 'En la parte superior encontrarás los filtros de fecha para analizar tu información.',
        list: [
          'Fecha Inicio: Es el día desde el cual deseas analizar tu información.',
          'Fecha Fin: Es el día hasta donde quieres consultar los datos.',
        ],
        image: '/images/manual/image2.jpeg',
        imageAlt: 'Filtro de fechas del Dashboard',
      },
      {
        subtitle: 'Botón Actualizar',
        text: 'Este botón sirve para aplicar el rango de fechas seleccionado y actualizar todos los datos, gráficas y métricas del Dashboard en base a esas fechas.',
        note: 'Siempre que cambies las fechas, debes presionar Actualizar para ver los nuevos resultados.',
      },
      {
        subtitle: 'Tarjetas de Resumen (Indicadores)',
        text: 'Son los cuadros que muestran los números principales de tu negocio.',
        image: '/images/manual/image3.jpeg',
        imageAlt: 'Tarjetas de resumen del Dashboard',
      },
      {
        subtitle: 'Total Facturas',
        text: 'Indica la cantidad total de facturas emitidas en el período seleccionado. Ejemplo: 269 facturas significa que en ese rango de fechas se han generado 269 documentos. Debajo ves un porcentaje comparado con el período anterior.',
      },
      {
        subtitle: 'Ingresos del período',
        text: 'Muestra el total de dinero facturado. Ejemplo: $513,914.75 = suma total de todas las facturas emitidas en ese período. También incluye una comparación porcentual con el período anterior.',
      },
      {
        subtitle: 'Facturas Pendientes',
        text: 'Indica cuántas facturas aún no han sido procesadas o aprobadas por la DGI. Si este número es alto, se recomienda revisarlas en la sección de Facturas.',
      },
      {
        subtitle: 'Facturas Aprobadas',
        text: 'Muestra la cantidad de facturas que han sido aceptadas por la DGI exitosamente. Si muestra 0 aprobadas puede indicar que las facturas están muy recientes, hubo rechazos, o hay algún problema en la validación.',
      },
      {
        subtitle: 'Tasa de Aprobación',
        text: 'Representa el porcentaje de facturas aprobadas respecto al total emitido. Si esta tasa es baja, hay que revisar errores frecuentes y validar datos fiscales y configuración.',
        image: '/images/manual/image5.jpeg',
        imageAlt: 'Métricas adicionales del Dashboard',
      },
      {
        subtitle: 'Tiempo Promedio de Aprobación',
        text: 'Mide cuánto tiempo tarda la DGI en aprobar una factura desde que la emites. Cuando existan aprobaciones, aquí verás el promedio en horas.',
      },
      {
        subtitle: 'Facturación Proyectada',
        text: 'Es una estimación de lo que facturarías al final del período si mantienes el mismo ritmo.',
      },
      {
        subtitle: 'Gráfica: Facturación Mensual',
        text: 'Esta gráfica muestra la cantidad de facturas generadas en los últimos meses. En el eje horizontal verás los meses y en el eje vertical la cantidad de facturas. Cada barra azul representa el total de facturas emitidas en ese mes.',
        list: [
          'Visualizar el crecimiento de tu negocio.',
          'Detectar meses con más o menos actividad.',
          'Comparar fácilmente tu desempeño mensual.',
        ],
        image: '/images/manual/image6.jpeg',
        imageAlt: 'Gráficas del Dashboard',
      },
      {
        subtitle: 'Gráfica: Distribución por Estado',
        text: 'Esta gráfica circular muestra el estado actual de tus facturas.',
        list: [
          'Pending (Pendientes): Facturas en proceso de validación por la DGI.',
          'Rejected (Rechazadas): Facturas rechazadas por errores en la información (RUC incorrecto, ITBMS mal calculado, datos incompletos).',
          'Others (Otros): Incluye otros estados menos frecuentes.',
        ],
      },
      {
        subtitle: 'Top 5 Clientes',
        text: 'Muestra los clientes con mayor facturación en el período seleccionado.',
        list: [
          'Cliente: Nombre y RUC/cédula del cliente.',
          'Facturas: Cantidad de facturas emitidas a ese cliente.',
          'Total: Monto total facturado.',
          'Promedio: Promedio facturado por cada documento.',
        ],
        image: '/images/manual/image7.jpeg',
        imageAlt: 'Top 5 Clientes y Tipos de Documento',
      },
      {
        subtitle: 'Top 5 Tipos de Documento',
        text: 'Muestra los tipos de documentos más utilizados y su impacto en la facturación.',
        list: [
          'Tipo: Código del tipo de documento (ejemplo: 01 = factura).',
          'Cantidad: Número de documentos emitidos de ese tipo.',
          'Total: Suma total facturada con ese tipo.',
        ],
      },
    ],
  },
  {
    id: 'facturas-activas',
    title: '2. Facturas Activas / Emitidas',
    icon: <DocumentTextIcon className="h-6 w-6" />,
    content: [
      {
        text: 'Desde esta sección puedes consultar, buscar y ver el detalle de todas las facturas emitidas por tu empresa.',
        image: '/images/manual/image8.png',
        imageAlt: 'Pantalla de Facturas Emitidas',
      },
      {
        subtitle: 'Barra de búsqueda',
        text: 'Número de factura o cliente: Permite buscar una factura específica escribiendo el número del documento o el nombre del cliente.',
      },
      {
        subtitle: 'Filtros por fecha',
        list: [
          'Fecha Inicio: Día desde donde deseas iniciar la consulta.',
          'Fecha Fin: Día hasta donde quieres ver las facturas.',
          'Botón Buscar: Actualiza la lista según los filtros aplicados.',
        ],
      },
      {
        subtitle: 'Tabla de facturas',
        text: 'Cada fila representa una factura emitida. Las columnas muestran:',
        list: [
          '# Documento: Número único de la factura.',
          'Tipo: Código del tipo de documento (ej. 01 = factura).',
          'Cliente: Nombre del cliente.',
          'RUC/Cédula: Identificación del cliente.',
          'Monto: Total facturado.',
          'Fecha: Fecha y hora en que se emitió.',
          'Estado: Indica si está procesada, pendiente o rechazada.',
          'Acciones → Ver detalles: Abre la factura para ver toda la información.',
        ],
      },
    ],
  },
  {
    id: 'archivos-historicos',
    title: '3. Archivos Históricos',
    icon: <ArchiveBoxIcon className="h-6 w-6" />,
    content: [
      {
        text: 'En esta pantalla puedes consultar todas las facturas con más de 90 días de antigüedad.',
        image: '/images/manual/image10.png',
        imageAlt: 'Pantalla de Archivos Históricos',
      },
      {
        subtitle: 'Filtros de búsqueda',
        text: 'Usa estos campos para encontrar documentos específicos:',
        list: [
          'CUFE: Busca por el código único de factura electrónica.',
          'Nombre del Cliente: Filtra por el nombre del cliente.',
          'Número de Documento: Busca por el número exacto de factura.',
          'RUC del Cliente: Filtra por el RUC o cédula del cliente.',
          'Estado de la Factura: Selecciona entre aprobado, rechazado, pendiente o todos.',
          'Tipo de Documento: Filtra por tipo de documento (factura, nota de crédito, etc.).',
          'Código de tienda: Filtra por un punto o sucursal específica.',
          'Punto de venta: Número de PV asociado al documento.',
          'Fecha inicio / Fecha fin: Define el rango de fechas para la búsqueda.',
        ],
      },
      {
        subtitle: 'Botón Buscar',
        text: 'Actualiza los resultados según los filtros seleccionados.',
      },
    ],
  },
  {
    id: 'nueva-factura',
    title: '4. Nueva Factura',
    icon: <PlusCircleIcon className="h-6 w-6" />,
    content: [
      {
        text: 'Esta sección permite crear una factura electrónica paso a paso, cumpliendo con los requisitos de la DGI.',
        image: '/images/manual/image12.jpeg',
        imageAlt: 'Pantalla de Nueva Factura',
      },
      {
        subtitle: 'Información del Emisor y Receptor',
        text: 'En este primer paso se completa la información básica de la factura.',
      },
      {
        subtitle: 'Información de la empresa emisora',
        text: 'Muestra automáticamente los datos fiscales de la empresa emisora:',
        list: [
          'Nombre',
          'RUC y DV',
          'Sucursal y punto de facturación',
        ],
        note: 'Estos datos provienen de la configuración del portal y no requieren edición manual.',
      },
      {
        subtitle: 'Información del receptor',
        text: 'Aquí se ingresan los datos del cliente al que se le emitirá la factura:',
        list: [
          'Tipo de receptor',
          'Nombre',
          'RUC/ID y DV',
          'Correo electrónico',
          'Teléfono',
          'Dirección',
        ],
      },
    ],
  },
  {
    id: 'productos',
    title: '5. Productos (Catálogo)',
    icon: <CubeIcon className="h-6 w-6" />,
    content: [
      {
        text: 'Catálogo donde puedes gestionar todos los productos y servicios que utilizarás al emitir facturas.',
        image: '/images/manual/image13.png',
        imageAlt: 'Catálogo de Productos',
      },
      {
        subtitle: 'Búsqueda y filtros',
        list: [
          'Nombre / Código: Cambia entre búsqueda por nombre o por código SKU.',
          'Barra de búsqueda: Permite buscar un producto escribiendo mínimo 3 caracteres.',
        ],
      },
      {
        subtitle: 'Botón "Nuevo Producto"',
        text: 'Te permite crear un producto o servicio nuevo para usarlo en facturas.',
      },
      {
        subtitle: 'Tabla de productos',
        text: 'Cada fila muestra la información del producto registrado:',
        list: [
          'SKU: Código único del producto o servicio.',
          'Nombre: Nombre del artículo (y descripción breve).',
          'Tipo: Producto o Servicio.',
          'Categoría: Categoría asignada.',
          'Precio: Precio unitario.',
          'Código de impuesto: Impuesto aplicable (ej. ITBMS7, ITBMS15).',
        ],
      },
    ],
  },
  {
    id: 'nuevo-producto',
    title: '6. Nuevo Producto',
    icon: <PlusCircleIcon className="h-6 w-6" />,
    content: [
      {
        text: 'Formulario para crear un nuevo producto o servicio que podrá ser usado al emitir facturas.',
        image: '/images/manual/image14.jpeg',
        imageAlt: 'Formulario de Nuevo Producto',
      },
      {
        subtitle: 'Campos del formulario',
        list: [
          'Código: Identificador único del producto (SKU).',
          'Categoría: Selecciona si pertenece a "Productos" o "Servicios".',
          'Nombre del producto: Nombre visible del producto o servicio.',
          'Descripción: Texto descriptivo del producto o servicio.',
        ],
      },
      {
        subtitle: 'Datos de venta',
        list: [
          'Precio: Precio unitario.',
          'Impuesto: Selecciona el porcentaje de impuesto aplicable (ej. 7%).',
          'Unidad: Unidad de medida (unidad, caja, paquete, etc.).',
          'Stock: Cantidad disponible (solo aplica para productos físicos).',
        ],
      },
      {
        subtitle: 'Opcionales',
        list: [
          'Segmento: Clasificación general del producto (opcional).',
          'Familia: Subcategoría vinculada al segmento (opcional).',
          'Producto Activo: Marca si el producto está disponible para usar.',
        ],
      },
      {
        subtitle: 'Acciones',
        list: [
          'Crear Producto: Guarda el nuevo producto.',
          'Cancelar: Cierra el formulario sin guardar.',
        ],
      },
    ],
  },
  {
    id: 'reportes',
    title: '7. Reportes',
    icon: <ChartBarIcon className="h-6 w-6" />,
    content: [
      {
        text: 'En esta sección puedes generar reportes en Excel o PDF con información de tus facturas emitidas.',
        image: '/images/manual/image15.jpeg',
        imageAlt: 'Pantalla de Reportes',
      },
      {
        subtitle: '1. Documentos emitidos (Excel)',
        text: 'Genera un archivo Excel con todas las facturas emitidas en una fecha específica.',
        list: [
          'Fecha: Selecciona el día que deseas consultar.',
          'Botón Generar reporte: Descarga el Excel con las facturas emitidas correctamente en esa fecha.',
        ],
      },
      {
        subtitle: '2. Reporte Z (PDF)',
        text: 'Genera un PDF con el total de facturas emitidas dentro de un rango de fechas.',
        list: [
          'Fecha inicio: Primer día del período a consultar.',
          'Fecha fin: Último día del período.',
          'Filtro por sucursales (opcional): Permite seleccionar una o varias sucursales para limitar el reporte.',
          'Botón Generar reporte: Descarga el PDF con el resumen completo del período seleccionado.',
        ],
      },
      {
        subtitle: '3. Balance General (Excel)',
        text: 'Genera un archivo Excel con todas las facturas registradas en el sistema dentro de un rango de tiempo.',
        list: [
          'Fecha inicio: Día desde el cual quieres comenzar la consulta.',
          'Fecha fin: Día hasta donde deseas obtener los datos.',
          'Filtro por sucursales (opcional): Puedes seleccionar una o varias sucursales para limitar el reporte.',
          'Generar reporte: Crea y descarga el archivo Excel con la información del período seleccionado.',
        ],
        image: '/images/manual/image16.jpeg',
        imageAlt: 'Balance General',
      },
    ],
  },
  {
    id: 'mi-empresa',
    title: '8. Mi Empresa',
    icon: <BuildingOfficeIcon className="h-6 w-6" />,
    content: [
      {
        subtitle: 'Información de Contacto',
        text: 'En esta sección puedes actualizar los datos de contacto oficiales de la empresa.',
        list: [
          'Email: Correo electrónico principal para notificaciones y uso administrativo.',
          'Teléfono: Número de contacto de la empresa.',
          'País: Selecciona el país en el que opera la empresa.',
          'Dirección: Dirección física o fiscal registrada.',
        ],
        image: '/images/manual/image17.jpeg',
        imageAlt: 'Información de Contacto',
      },
      {
        subtitle: 'Editar Empresa',
        text: 'Desde esta sección puedes actualizar la información legal y visual de tu empresa dentro del sistema.',
        image: '/images/manual/image18.jpeg',
        imageAlt: 'Editar Empresa',
      },
      {
        subtitle: 'Logo de la Empresa',
        list: [
          'Seleccionar imagen: Permite cargar o cambiar el logo.',
          'Formatos permitidos: JPG, PNG',
          'Tamaño máximo: 1MB',
        ],
      },
      {
        subtitle: 'Información Legal',
        text: 'Campos para configurar los datos fiscales utilizados en las facturas:',
        list: [
          'Tipo de Contribuyente: Selecciona si es Persona Natural o Jurídica.',
          'RUC: Número de Registro Único de Contribuyente.',
          'DV: Dígito verificador del RUC.',
          'Razón Social: Nombre legal de la empresa.',
          'Nombre Comercial: Nombre público con el que opera la empresa.',
        ],
      },
    ],
  },
  {
    id: 'configuracion',
    title: '9. Configuración Administrativa',
    icon: <Cog6ToothIcon className="h-6 w-6" />,
    content: [
      {
        subtitle: 'Configuración General',
        text: 'En esta sección puedes ajustar los parámetros principales relacionados con la emisión de facturas.',
        image: '/images/manual/image19.jpeg',
        imageAlt: 'Configuración General',
      },
      {
        subtitle: '1. Prefijo de Facturas',
        text: 'Campo donde puedes definir un prefijo que aparecerá antes del número de cada factura emitida. Ejemplo: FE-00001, FAC-00001, etc.',
      },
      {
        subtitle: '2. Tasa de Impuesto por Defecto (%)',
        text: 'Selecciona el porcentaje de impuesto que se aplicará automáticamente al crear nuevas facturas. Este valor puede ser ajustado manualmente por factura si es necesario.',
      },
      {
        subtitle: '3. Auto-aprobar Facturas (opcional)',
        text: 'Al activar esta opción, las facturas se aprobarán automáticamente sin revisión manual. Recomendado solo si tu proceso de facturación no requiere validaciones previas.',
      },
      {
        subtitle: 'Notificaciones y Seguridad',
        text: 'En esta sección puedes gestionar cómo deseas recibir avisos del sistema y configurar medidas adicionales de protección para tu cuenta.',
        list: [
          'Notificaciones por email: Activa esta opción para recibir alertas y actualizaciones importantes directamente en tu correo electrónico.',
          'Autenticación de dos factores (2FA): Si se habilita, el sistema solicitará un código de verificación adicional al iniciar sesión.',
        ],
        image: '/images/manual/image20.jpeg',
        imageAlt: 'Notificaciones y Seguridad',
      },
      {
        subtitle: 'Información Fiscal y Certificados',
        text: 'En esta sección puedes visualizar los datos fiscales registrados de tu empresa y el estado de los certificados utilizados.',
        list: [
          'Nombre: Razón social de la empresa.',
          'Nombre Comercial: Nombre utilizado públicamente.',
          'RUC: Registro Único de Contribuyente.',
          'DV: Dígito verificador del RUC.',
          'Tipo de Contribuyente: Persona Natural o Persona Jurídica.',
        ],
        image: '/images/manual/image21.png',
        imageAlt: 'Información Fiscal y Certificados',
      },
      {
        subtitle: 'Certificados Digitales',
        text: 'En esta sección puedes visualizar el estado y los datos técnicos de los certificados digitales utilizados para la emisión de facturas electrónicas.',
        list: [
          'Autenticación: Certificado utilizado para validar la conexión con los servicios de la DGI.',
          'Firma: Certificado utilizado para firmar digitalmente cada factura emitida.',
          'Secret JWT: Token de seguridad utilizado para la autenticación del sistema.',
          'Certificate Key (Encriptada): Contraseña del certificado, almacenada de forma segura.',
        ],
        image: '/images/manual/image24.jpeg',
        imageAlt: 'Certificados Digitales',
        note: 'Cuando aparece el mensaje "Los certificados digitales están configurados correctamente", el sistema está listo para operar.',
      },
      {
        subtitle: 'Actualizar Certificados',
        text: 'En esta pantalla puedes cargar o reemplazar los certificados digitales requeridos para emitir facturas electrónicas ante la DGI.',
        list: [
          'Certificado de Autenticación: Archivo en formato .pfx o .p12 utilizado para autenticar la conexión con los servicios de la DGI.',
          'Certificado de Firma: Archivo .pfx o .p12 responsable de firmar digitalmente cada factura emitida.',
          'Certificate Key (Password): Contraseña del archivo del certificado. Es obligatoria para desencriptar y validar ambos certificados cargados.',
        ],
        image: '/images/manual/image25.jpeg',
        imageAlt: 'Actualizar Certificados',
      },
      {
        subtitle: 'Templates - Configuración de Email',
        text: 'Esta sección permite personalizar el contenido y estilo de los correos electrónicos enviados a los clientes junto con la factura electrónica.',
        list: [
          'Envío Automático de Email: Activa esta opción para que el sistema envíe automáticamente un correo cada vez que se genera una factura electrónica.',
          'Descripción del Template: Texto descriptivo que aparecerá dentro del correo enviado al cliente.',
          'URL de Acción del Template: Espacio destinado para agregar una URL opcional que pueda acompañar el correo.',
        ],
        image: '/images/manual/image26.jpeg',
        imageAlt: 'Templates de Email',
      },
    ],
  },
  {
    id: 'sucursales',
    title: '10. Gestión de Sucursales',
    icon: <BuildingStorefrontIcon className="h-6 w-6" />,
    content: [
      {
        text: 'En esta sección puedes administrar las sucursales registradas de tu empresa.',
        image: '/images/manual/image28.png',
        imageAlt: 'Gestión de Sucursales',
      },
      {
        subtitle: 'Listado de Sucursales',
        text: 'Se muestra un listado con la información principal de cada sucursal:',
        list: [
          'Código: Identificador de la sucursal.',
          'Nombre: Nombre asignado a la sucursal.',
          'Ubicación: Dirección registrada.',
          'Contacto: Información de contacto (si aplica).',
          'Tipo de dirección: Uso de la dirección (ej. Trabajo).',
          'Tipo: Sucursal o Principal.',
          'Estado: Activa o inactiva.',
        ],
      },
      {
        subtitle: 'Buscar sucursal',
        text: 'Utiliza el campo de búsqueda para encontrar una sucursal por nombre o código.',
      },
      {
        subtitle: 'Nueva Sucursal',
        text: 'Haz clic en Nueva Sucursal para registrar una nueva sucursal en el sistema.',
      },
      {
        subtitle: 'Acciones',
        list: [
          'Editar: Modifica la información de la sucursal.',
          'Eliminar: Elimina la sucursal seleccionada (si aplica).',
        ],
      },
    ],
  },
  {
    id: 'usuarios',
    title: '11. Gestión de Usuarios',
    icon: <UsersIcon className="h-6 w-6" />,
    content: [
      {
        text: 'En esta sección puedes administrar los usuarios que tendrán acceso al sistema de facturación.',
        image: '/images/manual/image29.jpeg',
        imageAlt: 'Gestión de Usuarios',
      },
      {
        subtitle: 'Usuarios del Sistema',
        text: 'Desde aquí puedes:',
        list: [
          'Visualizar los usuarios creados.',
          'Buscar usuarios por nombre o correo electrónico.',
          'Crear nuevos accesos al sistema.',
        ],
        note: 'Si no existen usuarios registrados, el sistema mostrará el mensaje "No hay usuarios".',
      },
      {
        subtitle: 'Nuevo Usuario',
        text: 'Haz clic en Nuevo Usuario para crear un usuario y asignarle acceso al sistema.',
      },
    ],
  },
  {
    id: 'planes',
    title: '12. Planes y Suscripción',
    icon: <CreditCardIcon className="h-6 w-6" />,
    content: [
      {
        text: 'En esta pantalla puedes visualizar y gestionar los planes disponibles de Factura Libre.',
        image: '/images/manual/image32.jpeg',
        imageAlt: 'Planes y Suscripción',
      },
      {
        subtitle: 'Uso sin Suscripción Activa',
        text: 'Aunque no tengas una suscripción activa, el sistema te permite emitir hasta 120 facturas de forma gratuita. No es necesario suscribirte para comenzar a facturar dentro de este límite.',
      },
      {
        subtitle: 'Planes Disponibles',
        text: 'Una vez superadas las 120 facturas gratuitas, puedes elegir entre los siguientes planes según las necesidades de tu negocio:',
        list: [
          'Plan Free: Ideal para iniciar operaciones con bajo volumen de facturación.',
          'Plan Emprende: Pensado para pequeños negocios en crecimiento.',
          'Plan Ilimitado: Recomendado para empresas con alto volumen de facturación.',
          'Plan Premium: Diseñado para empresas que requieren mayor personalización y soporte prioritario.',
        ],
      },
      {
        subtitle: 'Activación de Plan',
        text: 'Desde esta pantalla puedes:',
        list: [
          'Activar el plan gratuito.',
          'Solicitar la activación de un plan superior mediante el botón Contactar para Activar.',
        ],
      },
    ],
  },
];

// Image Modal Component
function ImageModal({
  isOpen,
  onClose,
  imageSrc,
  imageAlt
}: {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  imageAlt: string;
}) {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
      >
        <XMarkIcon className="h-8 w-8 text-white" />
      </button>
      <img
        src={imageSrc}
        alt={imageAlt}
        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

function ManualContentItem({ content }: { content: ManualContent }) {
  const [imageModalOpen, setImageModalOpen] = useState(false);

  return (
    <div className="space-y-4">
      {content.subtitle && (
        <h4 className="text-lg font-semibold text-secondary-800 dark:text-secondary-100 border-l-4 border-primary-500 pl-3">
          {content.subtitle}
        </h4>
      )}

      {content.text && (
        <p className="text-secondary-600 dark:text-secondary-400 leading-relaxed">
          {content.text}
        </p>
      )}

      {content.list && (
        <ul className="space-y-2 ml-1">
          {content.list.map((item, index) => (
            <li key={index} className="flex items-start gap-3 text-secondary-600 dark:text-secondary-400">
              <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-primary-500 mt-2" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}

      {content.note && (
        <div className="flex gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <p className="text-amber-800 dark:text-amber-200 text-sm">
            <strong className="font-semibold">Importante:</strong> {content.note}
          </p>
        </div>
      )}

      {content.image && (
        <>
          <div className="flex flex-col items-center my-6">
            <div
              className="relative group cursor-zoom-in max-w-lg"
              onClick={() => setImageModalOpen(true)}
            >
              <img
                src={content.image}
                alt={content.imageAlt || 'Imagen del manual'}
                className="rounded-xl border-2 border-secondary-200 dark:border-secondary-600 shadow-lg hover:shadow-xl transition-shadow duration-300"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-xl transition-colors duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 dark:bg-secondary-800/90 rounded-full p-3 shadow-lg">
                  <MagnifyingGlassIcon className="h-6 w-6 text-secondary-700 dark:text-secondary-300" />
                </div>
              </div>
            </div>
            {content.imageAlt && (
              <p className="mt-3 text-sm text-secondary-500 dark:text-secondary-400 italic text-center">
                {content.imageAlt}
              </p>
            )}
          </div>
          <ImageModal
            isOpen={imageModalOpen}
            onClose={() => setImageModalOpen(false)}
            imageSrc={content.image}
            imageAlt={content.imageAlt || 'Imagen del manual'}
          />
        </>
      )}
    </div>
  );
}

function ManualSectionCard({ section }: { section: ManualSection }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Card className="overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5 text-left hover:from-primary-700 hover:to-primary-800 transition-all duration-300"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-white/20 rounded-lg">
              <div className="text-white">{section.icon}</div>
            </div>
            <h2 className="text-lg font-bold text-white">{section.title}</h2>
          </div>
          <div className={cn(
            "p-1 rounded-full bg-white/20 transition-transform duration-300",
            isOpen && "rotate-180"
          )}>
            <ChevronDownIcon className="h-5 w-5 text-white" />
          </div>
        </div>
      </button>
      <div
        className={cn(
          'overflow-hidden transition-all duration-500 ease-in-out',
          isOpen ? 'max-h-[10000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <CardBody className="p-6 lg:p-8 space-y-8 bg-white dark:bg-secondary-800">
          <div className="max-w-4xl mx-auto space-y-8">
            {section.content.map((content, index) => (
              <div key={index}>
                <ManualContentItem content={content} />
                {index < section.content.length - 1 && (
                  <hr className="mt-8 border-secondary-200 dark:border-secondary-700" />
                )}
              </div>
            ))}
          </div>
        </CardBody>
      </div>
    </Card>
  );
}

export function UserManualPage() {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredSections = manualData.filter((section) => {
    const searchLower = searchTerm.toLowerCase();
    const titleMatch = section.title.toLowerCase().includes(searchLower);
    const contentMatch = section.content.some(
      (c) =>
        c.subtitle?.toLowerCase().includes(searchLower) ||
        c.text?.toLowerCase().includes(searchLower) ||
        c.list?.some((item) => item.toLowerCase().includes(searchLower))
    );
    return titleMatch || contentMatch;
  });

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">
          Manual de Usuario
        </h1>
        <p className="text-lg text-secondary-600 dark:text-secondary-400">
          Guía completa para el uso del sistema Factura Libre
        </p>
      </div>

      {/* Search */}
      <div className="relative max-w-2xl mx-auto">
        <input
          type="text"
          placeholder="Buscar en el manual..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-5 py-4 pl-12 border-2 border-secondary-200 dark:border-secondary-600 rounded-xl bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all shadow-sm"
        />
        <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-400" />
      </div>

      {/* Manual Sections */}
      {filteredSections.length > 0 ? (
        <div className="space-y-6">
          {filteredSections.map((section) => (
            <div key={section.id} id={section.id} className="scroll-mt-4">
              <ManualSectionCard section={section} />
            </div>
          ))}
        </div>
      ) : (
        <Card className="shadow-md">
          <CardBody className="text-center py-16">
            <MagnifyingGlassIcon className="h-12 w-12 text-secondary-300 dark:text-secondary-600 mx-auto mb-4" />
            <p className="text-secondary-500 dark:text-secondary-400 text-lg">
              No se encontraron resultados para "<span className="font-medium">{searchTerm}</span>"
            </p>
            <p className="text-secondary-400 dark:text-secondary-500 text-sm mt-2">
              Intenta con otros términos de búsqueda
            </p>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
